import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppState } from '@/ui/AppState';
import { navigate, useRoute } from '@/ui/router';
import { CategoryChip, EmptyState, FlagBanner, SessionChip } from '@/ui/components/bits';
import { SessionPicker } from '@/ui/components/SessionPicker';
import { ModelAnswer, QuestionPrompts } from '@/ui/components/ModelAnswer';
import { Explanation, optionLetter } from '@/ui/components/Explanation';
import { buildQueue } from '@/domain/queue';
import { isSingleSessionMode } from '@/domain/select';
import { recordAnswer } from '@/domain/practice';
import { clearRun, loadRun, runKey, saveRun } from '@/domain/resume';
import type { Item, StudyMode } from '@/domain/types';
import { modeTitle, pickerHeading, pickerKind } from './StudyScreen';

export function QuizScreen() {
  const route = useRoute();
  const mode = (route.params.get('mode') ?? 'cumulative') as StudyMode;
  const sessionId = route.params.get('session') ?? undefined;
  const category = route.params.get('category') ?? undefined;
  /**
   * A part of the mid-term paper is several categories at once, so the quiz
   * takes a comma-separated list as well as a single name. The raw string is
   * what identifies the run, which is what lets a drill be resumed.
   */
  const categories = route.params.get('categories') ?? undefined;
  /** A heading the caller already has a better name for, e.g. a paper part. */
  const title = route.params.get('title') ?? undefined;
  /**
   * Force the textbook chapters into the draw. Cumulative leaves them out by
   * default so his own classes are not buried, but a drill aimed at one part
   * of the paper wants the whole book: that is where most of the questions
   * for chapters 1-6 are.
   */
  const textbook = route.params.get('textbook') === '1';
  const nonce = route.params.get('t') ?? '';

  if (isSingleSessionMode(mode) && !sessionId) {
    return (
      <div className="stack">
        <h1>{pickerHeading(mode)}</h1>
        <p className="muted">Pick a session to quiz yourself on.</p>
        <SessionPicker
          kind={pickerKind(mode)}
          want="question"
          onPick={(id) => navigate('/quiz', { mode, session: id })}
        />
      </div>
    );
  }

  return (
    <QuizLoader
      key={`${mode}|${sessionId ?? ''}|${category ?? ''}|${categories ?? ''}|${nonce}`}
      mode={mode}
      sessionId={sessionId}
      category={category}
      categories={categories}
      title={title}
      textbook={textbook}
      nonce={nonce}
    />
  );
}

interface Answer {
  correct: boolean;
  chosenIndex?: number;
}

interface Start {
  deck: Item[];
  index: number;
  answers: Record<string, Answer>;
  /** True when this deck was picked up rather than drawn. */
  resumed: boolean;
}

interface RunnerProps {
  mode: StudyMode;
  sessionId?: string;
  category?: string;
  /** Comma-separated, as it came from the URL. */
  categories?: string;
  title?: string;
  /** Draw from the textbook chapters too, whatever the usual setting says. */
  textbook?: boolean;
  nonce: string;
}

/**
 * Draws the deck, or picks up the one left half-finished. Reading the saved
 * place is a database read, so it happens here and the runner below starts
 * from a settled deck — which keeps its position in ordinary useState.
 */
function QuizLoader(props: RunnerProps) {
  const { mode, sessionId, category, categories, textbook, nonce } = props;
  const categoryList = categories ? categories.split(',').filter(Boolean) : undefined;
  // One key for the saved place, whether the drill was one category or ten.
  const runCategory = categories ?? category;
  const state = useAppState();
  const latest = useRef(state);
  latest.current = state;

  const [start, setStart] = useState<Start | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const now = latest.current;
      const resumed = await loadRun(
        runKey({ want: 'question', mode, sessionId, category: runCategory }),
        nonce,
        now.items,
      );
      if (cancelled) return;
      if (resumed) {
        setStart({
          deck: resumed.items,
          index: resumed.index,
          answers: resumed.answers,
          resumed: true,
        });
        return;
      }
      setStart({
        deck: buildQueue({
          items: now.items,
          sessions: now.sessions,
          mode,
          sessionId,
          category,
          categories: categoryList,
          want: 'question',
          progress: now.progress,
          levels: now.levels,
          override: now.local?.difficultyOverride ?? 'auto',
          now: Date.now(),
          limit: isSingleSessionMode(mode) ? undefined : (now.local?.cumulativeQuizCount ?? 20),
          focusLatestClass: mode === 'cumulative' && Boolean(now.local?.focusLatestClass),
          includeTextbook: textbook || Boolean(now.local?.includeTextbookInCumulative),
        }).items,
        index: 0,
        answers: {},
        resumed: false,
      });
    })();
    return () => {
      cancelled = true;
    };
    // Drawn once, on mount. A new key remounts this whole component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!start) return <p className="muted">Loading…</p>;
  return <QuizRunner {...props} start={start} />;
}

function QuizRunner({ mode, sessionId, category, categories, title, nonce, start }: RunnerProps & { start: Start }) {
  const state = useAppState();
  const { sessions, reload } = state;
  const [index, setIndex] = useState(start.index);
  const [answers, setAnswers] = useState<Record<string, Answer>>(start.answers);
  const [typed, setTyped] = useState('');
  const [revealed, setRevealed] = useState(false);
  /**
   * Multiple choice is asked blind: the options stay hidden until he asks for
   * them, so he has to produce the answer rather than recognise it. Reset per
   * question, like `typed` and `revealed`.
   */
  const [optionsShown, setOptionsShown] = useState(false);
  const [finished, setFinished] = useState(false);
  const answeredRef = useRef<Set<string>>(new Set(Object.keys(start.answers)));

  const deck = start.deck;
  // Must match the key the loader read from, so a drill of several
  // categories saves its place under the same name it was found by.
  const key = runKey({ want: 'question', mode, sessionId, category: categories ?? category });

  /**
   * The place is saved after every answer and every move, so closing the app
   * mid-quiz and coming back lands on the same question. Finishing clears it:
   * a run you have seen the results of is not one to pick up again.
   */
  useEffect(() => {
    if (deck.length === 0) return;
    if (finished) {
      void clearRun(key);
      return;
    }
    void saveRun({
      want: 'question',
      mode,
      sessionId,
      category: categories ?? category,
      nonce,
      items: deck,
      index,
      answers,
    });
  }, [key, deck, index, answers, finished, mode, sessionId, category, nonce]);
  const current = deck[index];
  const answered = current ? answers[current.id] : undefined;
  // On by default, as he asked: recognising an option is not the same as
  // knowing the answer. Switchable in Settings.
  const blind = state.local?.blindMultipleChoice ?? true;

  const next = useCallback(() => {
    setTyped('');
    setRevealed(false);
    setOptionsShown(false);
    if (index >= deck.length - 1) setFinished(true);
    else setIndex((i) => i + 1);
  }, [index, deck.length]);

  const record = useCallback(
    async (item: Item, correct: boolean, chosenIndex?: number) => {
      setAnswers((a) => ({ ...a, [item.id]: { correct, ...(chosenIndex !== undefined ? { chosenIndex } : {}) } }));
      answeredRef.current.add(item.id);
      await recordAnswer({ item, correct, mode });
      await reload();
    },
    [mode, reload],
  );

  if (deck.length === 0) {
    return (
      <EmptyState icon="✓" title="Nothing to quiz here">
        {(title ?? category)
          ? `No questions in ${title ?? category} at the moment.`
          : 'This selection has no questions yet.'}
      </EmptyState>
    );
  }

  if (finished) {
    return (
      <QuizSummary
        deck={deck}
        answers={answers}
        mode={mode}
        sessionId={sessionId}
        category={category}
        categories={categories}
      />
    );
  }

  if (!current) return null;

  const sessionLabel = sessions.find((s) => s.id === current.sessionId)?.shortLabel ?? '';
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="stack">
      <div className="spread">
        <h1>{title ?? category ?? modeTitle(mode, sessions, sessionId)}</h1>
        <button
          className="btn btn--sm"
          onClick={() => navigate('/study', { mode, session: sessionId, category })}
        >
          Cards instead
        </button>
      </div>

      {start.resumed && index === start.index ? (
        <div className="banner banner--ok">
          <strong>Picked up where you left off.</strong>
          <div>
            Question {start.index + 1} of {deck.length}, with your earlier answers kept.{' '}
            <button
              className="btn btn--sm"
              onClick={() => {
                void clearRun(key);
                navigate('/quiz', { mode, session: sessionId, category, categories, t: String(Date.now()) });
              }}
            >
              Start a fresh set
            </button>
          </div>
        </div>
      ) : null}

      <div
        className="progress"
        role="progressbar"
        aria-valuenow={answeredCount}
        aria-valuemin={0}
        aria-valuemax={deck.length}
      >
        <div className="progress__fill" style={{ width: `${(answeredCount / deck.length) * 100}%` }} />
      </div>
      <div className="counter">
        Question {index + 1} of {deck.length}
      </div>

      <div className="card stack">
        <div className="flashcard__meta">
          <CategoryChip category={current.category} />
          {sessionLabel ? <SessionChip label={sessionLabel} /> : null}
        </div>
        <div className="question-text">{current.q}</div>

        {current.type === 'mc' && blind && !optionsShown && !answered ? (
          /* Asked blind: work it out before seeing anything to recognise. */
          <div className="stack">
            <label className="label" htmlFor="blind-answer">
              Your answer, before you see the options
            </label>
            <textarea
              id="blind-answer"
              className="input"
              style={{ minHeight: 96, resize: 'vertical' }}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
            {!revealed ? (
              <div className="row">
                <button className="btn btn--primary" onClick={() => setRevealed(true)}>
                  Check my answer
                </button>
                <button className="btn" onClick={() => setOptionsShown(true)}>
                  Show me the options
                </button>
              </div>
            ) : (
              <>
                <Explanation item={current} />
                <div className="row" style={{ justifyContent: 'center' }}>
                  <button className="btn btn--good" onClick={() => void record(current, true)}>
                    Got it
                  </button>
                  <button className="btn btn--bad" onClick={() => void record(current, false)}>
                    Missed it
                  </button>
                </div>
              </>
            )}
          </div>
        ) : current.type === 'mc' ? (
          <div className="stack">
            {blind && typed.trim() ? (
              <>
                <span className="flashcard__label">What you wrote first</span>
                <div className="sample">{typed}</div>
              </>
            ) : null}
            {(current.opts ?? []).map((opt, i) => {
              let cls = 'option';
              if (answered) {
                if (i === current.answerIndex) cls += ' option--correct';
                else if (i === answered.chosenIndex) cls += ' option--wrong';
              }
              return (
                <button
                  key={i}
                  className={cls}
                  disabled={Boolean(answered)}
                  onClick={() => void record(current, i === current.answerIndex, i)}
                >
                  <span className="option__key">{optionLetter(i)}</span>
                  <span className="option__text">{opt}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="stack">
            <QuestionPrompts item={current} />
            <label className="label" htmlFor="short-answer">
              Your answer
            </label>
            <textarea
              id="short-answer"
              className="input"
              style={{ minHeight: current.type === 'short' ? 96 : 160, resize: 'vertical' }}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              disabled={Boolean(answered)}
            />
            {!revealed ? (
              <button className="btn btn--primary" onClick={() => setRevealed(true)}>
                {current.type === 'work' ? 'Reveal the worked solution' : 'Reveal sample answer'}
              </button>
            ) : (
              <>
                <ModelAnswer item={current} />
                {!answered ? (
                  <div className="row" style={{ justifyContent: 'center' }}>
                    <button className="btn btn--good" onClick={() => void record(current, true)}>
                      Got it
                    </button>
                    <button className="btn btn--bad" onClick={() => void record(current, false)}>
                      Missed it
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        )}

        {answered ? (
          <>
            <div className={`banner banner--${answered.correct ? 'ok' : 'error'}`}>
              <strong>{answered.correct ? 'Correct' : 'Not right'}</strong>
            </div>
            {/* The reasoning is shown every time, right or wrong. */}
            <Explanation item={current} showModelAnswer={!revealed} />
            {current.flag ? <FlagBanner flag={current.flag} text={current.flagText} /> : null}
            <button className="btn btn--primary btn--block" onClick={next}>
              {index >= deck.length - 1 ? 'See results' : 'Next question'}
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

function QuizSummary({
  deck,
  answers,
  mode,
  sessionId,
  category,
  categories,
}: {
  deck: Item[];
  answers: Record<string, Answer>;
  mode: StudyMode;
  sessionId?: string;
  category?: string;
  categories?: string;
}) {
  const total = deck.length;
  const correct = deck.filter((i) => answers[i.id]?.correct).length;
  const pct = total ? Math.round((correct / total) * 100) : 0;

  const byCategory = useMemo(() => {
    const map = new Map<string, { correct: number; total: number }>();
    for (const item of deck) {
      const entry = map.get(item.category) ?? { correct: 0, total: 0 };
      entry.total += 1;
      if (answers[item.id]?.correct) entry.correct += 1;
      map.set(item.category, entry);
    }
    return [...map.entries()]
      .map(([name, v]) => ({ category: name, ...v, pct: Math.round((v.correct / v.total) * 100) }))
      .sort((a, b) => a.pct - b.pct);
  }, [deck, answers]);

  const weak = byCategory.filter((c) => c.pct < 100);

  return (
    <div className="stack">
      <h1>Results</h1>
      <div className="card stack">
        <div className="score">
          {correct} / {total}
        </div>
        <div className="counter">{pct}%</div>
      </div>

      <h2 className="section-title">By topic</h2>
      <ul className="list">
        {byCategory.map((c) => (
          <li key={c.category} className="card">
            <div className="spread">
              <CategoryChip category={c.category} />
              <span className="mono">
                {c.correct}/{c.total} · {c.pct}%
              </span>
            </div>
          </li>
        ))}
      </ul>

      {weak.length > 0 ? (
        <div className="banner banner--warn">
          <strong>Worth another look</strong>
          <div>{weak.map((w) => w.category).join(', ')}</div>
        </div>
      ) : (
        <div className="banner banner--ok">
          <strong>Clean sweep.</strong>
        </div>
      )}

      <div className="row">
        <button
          className="btn btn--primary"
          onClick={() => navigate('/quiz', { mode, session: sessionId, category, categories, t: String(Date.now()) })}
        >
          Go again
        </button>
        <button className="btn" onClick={() => navigate('/notebook')}>
          Mistake notebook
        </button>
        <button className="btn" onClick={() => navigate('/')}>
          Home
        </button>
      </div>
    </div>
  );
}
